import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import Modal from '../components/common/Modal';
import { useAuth } from '../context/AuthContext';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const pct = (achieved, target) => {
  if (!target || target === 0) return achieved > 0 ? 100 : 0;
  return Math.round((achieved / target) * 100);
};

const badgeColor = (p) => {
  if (p >= 100) return { background: '#dcfce7', color: '#15803d' };
  if (p >= 70)  return { background: '#fef9c3', color: '#854d0e' };
  return { background: '#fee2e2', color: '#991b1b' };
};

const ProgressBar = ({ achieved, target, color }) => {
  const p = Math.min(pct(achieved, target), 100);
  const barColor = p >= 100 ? '#16a34a' : p >= 70 ? '#ca8a04' : '#dc2626';
  return (
    <div style={{ marginTop: 4 }}>
      <div style={{ height: 5, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ width: `${p}%`, height: '100%', background: barColor, borderRadius: 4, transition: 'width 0.4s' }} />
      </div>
    </div>
  );
};

const defaultTargetForm = { target_amount: '', target_visits: '', target_new_customers: '', notes: '' };

export default function TVA() {
  const { user, isPrivileged } = useAuth();
  const canEdit = isPrivileged;

  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [rows, setRows] = useState([]);
  const [userFilter, setUserFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ open: false, user: null });
  const [form, setForm] = useState(defaultTargetForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/tva', { params: { year, month } });
      setRows(data.data || []);
    } catch (err) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => { load(); }, [load]);

  const openSetTarget = (row) => {
    setForm({
      target_amount: row.target_amount || '',
      target_visits: row.target_visits || '',
      target_new_customers: row.target_new_customers || '',
      notes: '',
    });
    setError('');
    setModal({ open: true, user: row });
  };

  const handleSave = async () => {
    setSaving(true); setError('');
    try {
      await api.put(`/tva/${modal.user.user_id}`, {
        year, month,
        target_amount: parseFloat(form.target_amount) || 0,
        target_visits: parseInt(form.target_visits) || 0,
        target_new_customers: parseInt(form.target_new_customers) || 0,
        notes: form.notes,
      });
      setModal({ open: false });
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save target.');
    } finally {
      setSaving(false);
    }
  };

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // For non-privileged: only show own row; for managers: allow filtering
  const visibleRows = isPrivileged
    ? (userFilter ? rows.filter(r => String(r.user_id) === userFilter) : rows)
    : rows.filter(r => String(r.user_id) === String(user?.id));

  // Totals row
  const totals = visibleRows.reduce((acc, r) => ({
    target_amount: acc.target_amount + r.target_amount,
    achieved_amount: acc.achieved_amount + r.achieved_amount,
    target_visits: acc.target_visits + r.target_visits,
    achieved_visits: acc.achieved_visits + r.achieved_visits,
    target_new_customers: acc.target_new_customers + r.target_new_customers,
    achieved_new_customers: acc.achieved_new_customers + r.achieved_new_customers,
  }), { target_amount: 0, achieved_amount: 0, target_visits: 0, achieved_visits: 0, target_new_customers: 0, achieved_new_customers: 0 });

  const yearOptions = [];
  for (let y = now.getFullYear() - 2; y <= now.getFullYear() + 1; y++) yearOptions.push(y);

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Target vs Achievement</h1>
          <p className="page-subtitle">Track monthly targets and achievements for your team</p>
        </div>
      </div>

      {/* Period selector */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-body" style={{ padding: '12px 16px' }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <label style={{ fontSize: '0.82rem', fontWeight: 500, color: 'var(--text-secondary)' }}>Month:</label>
              <select className="form-control form-select" style={{ width: 120 }} value={month} onChange={e => setMonth(parseInt(e.target.value))}>
                {MONTHS.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <label style={{ fontSize: '0.82rem', fontWeight: 500, color: 'var(--text-secondary)' }}>Year:</label>
              <select className="form-control form-select" style={{ width: 100 }} value={year} onChange={e => setYear(parseInt(e.target.value))}>
                {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            {isPrivileged && rows.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <label style={{ fontSize: '0.82rem', fontWeight: 500, color: 'var(--text-secondary)' }}>User:</label>
                <select className="form-control form-select" style={{ width: 180 }} value={userFilter} onChange={e => setUserFilter(e.target.value)}>
                  <option value="">All Users</option>
                  {rows.map(r => <option key={r.user_id} value={r.user_id}>{r.user_name}</option>)}
                </select>
              </div>
            )}
            <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
              Showing data for {MONTHS[month - 1]} {year}
              {!isPrivileged && ' (your data)'}
            </span>
          </div>
        </div>
      </div>

      {/* Summary cards */}
      {visibleRows.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 16 }}>
          {[
            { label: 'Total Sales Achievement', target: totals.target_amount, achieved: totals.achieved_amount, prefix: '₹', format: v => v >= 100000 ? `${(v/100000).toFixed(1)}L` : v.toLocaleString('en-IN') },
            { label: 'Total Visits', target: totals.target_visits, achieved: totals.achieved_visits, prefix: '', format: v => v },
            { label: 'New Customers', target: totals.target_new_customers, achieved: totals.achieved_new_customers, prefix: '', format: v => v },
          ].map((card, i) => {
            const p = pct(card.achieved, card.target);
            const bc = badgeColor(p);
            return (
              <div key={i} className="card">
                <div className="card-body" style={{ padding: '14px 16px' }}>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 500, marginBottom: 6 }}>{card.label}</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <div>
                      <span style={{ fontSize: '1.3rem', fontWeight: 700 }}>{card.prefix}{card.format(card.achieved)}</span>
                      <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginLeft: 4 }}>/ {card.prefix}{card.format(card.target)}</span>
                    </div>
                    <span style={{ fontSize: '0.78rem', fontWeight: 600, padding: '2px 8px', borderRadius: 20, ...bc }}>{p}%</span>
                  </div>
                  <ProgressBar achieved={card.achieved} target={card.target} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Table */}
      <div className="card">
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Role</th>
                <th>Sales (Target / Achieved)</th>
                <th>Visits (T / A)</th>
                <th>New Customers (T / A)</th>
                <th>Achievement %</th>
                {canEdit && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={canEdit ? 7 : 6}><div className="loading-overlay"><div className="spinner" /></div></td></tr>
              )}
              {!loading && visibleRows.length === 0 && (
                <tr><td colSpan={canEdit ? 7 : 6}><div className="empty-state"><p>No team members found.</p></div></td></tr>
              )}
              {!loading && visibleRows.map(row => {
                const amtPct = pct(row.achieved_amount, row.target_amount);
                const bc = badgeColor(amtPct);
                return (
                  <tr key={row.user_id}>
                    <td style={{ fontWeight: 600 }}>{row.user_name}</td>
                    <td><span className="badge badge-neutral" style={{ textTransform: 'uppercase', fontSize: '0.65rem' }}>{row.role}</span></td>
                    <td style={{ minWidth: 180 }}>
                      <div style={{ fontSize: '0.875rem' }}>
                        <span style={{ fontWeight: 600 }}>₹{row.achieved_amount.toLocaleString('en-IN')}</span>
                        <span style={{ color: 'var(--text-muted)' }}> / ₹{row.target_amount.toLocaleString('en-IN')}</span>
                      </div>
                      <ProgressBar achieved={row.achieved_amount} target={row.target_amount} />
                    </td>
                    <td style={{ fontSize: '0.875rem' }}>
                      <span style={{ fontWeight: 600 }}>{row.achieved_visits}</span>
                      <span style={{ color: 'var(--text-muted)' }}> / {row.target_visits}</span>
                    </td>
                    <td style={{ fontSize: '0.875rem' }}>
                      <span style={{ fontWeight: 600 }}>{row.achieved_new_customers}</span>
                      <span style={{ color: 'var(--text-muted)' }}> / {row.target_new_customers}</span>
                    </td>
                    <td>
                      <span style={{ fontSize: '0.8rem', fontWeight: 700, padding: '3px 10px', borderRadius: 20, ...bc }}>
                        {amtPct}%
                      </span>
                    </td>
                    {canEdit && (
                      <td>
                        <button
                          className="btn btn-ghost btn-icon"
                          data-tooltip="Set Target"
                          onClick={() => openSetTarget(row)}
                        >
                          <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
                            <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
                          </svg>
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Set Target Modal */}
      <Modal
        open={modal.open}
        onClose={() => setModal({ open: false })}
        title={`Set Target — ${modal.user?.user_name}`}
        footer={<>
          <button className="btn btn-secondary" onClick={() => setModal({ open: false })}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save Target'}</button>
        </>}
      >
        {error && <div className="alert alert-error">{error}</div>}
        <div style={{ marginBottom: 12, padding: '8px 12px', background: 'var(--primary-lighter)', borderRadius: 8, fontSize: '0.82rem', color: 'var(--primary-dark)' }}>
          Period: <strong>{MONTHS[month - 1]} {year}</strong>
        </div>
        <div className="form-group">
          <label className="form-label">Sales Target (₹)</label>
          <input className="form-control" type="number" min="0" step="0.01" value={form.target_amount} onChange={e => f('target_amount', e.target.value)} placeholder="0" />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Visit Target</label>
            <input className="form-control" type="number" min="0" value={form.target_visits} onChange={e => f('target_visits', e.target.value)} placeholder="0" />
          </div>
          <div className="form-group">
            <label className="form-label">New Customers Target</label>
            <input className="form-control" type="number" min="0" value={form.target_new_customers} onChange={e => f('target_new_customers', e.target.value)} placeholder="0" />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Notes</label>
          <textarea className="form-control" rows={2} value={form.notes} onChange={e => f('notes', e.target.value)} placeholder="Optional notes…" />
        </div>
      </Modal>
    </div>
  );
}
